/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid util/direction.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

// Read from the element rather than the document: a grid can sit inside an RTL
// subtree of an LTR page, which is why the vendored isRTL() (it checks
// documentElement.dir) is not the helper for any of this.
//
// Angular prerenders in Node, where there is no getComputedStyle — and no
// layout to have a direction. LTR is the right answer there: the server-side
// pass emits logical markup either way, and the client corrects on hydration.
export const isElementRTL = (element: Element | null | undefined): boolean =>
  typeof getComputedStyle === 'function' && element ?
    getComputedStyle(element).direction === 'rtl' :
    false

interface ResizeDirectionTable {
  options: { columnResizeDirection?: 'ltr' | 'rtl' }
  setOptions: (updater: (previous: { columnResizeDirection?: 'ltr' | 'rtl' }) => never) => void
}

// table-core copies the options object at construction, so the direction it
// captured goes stale the moment an app swaps language on a live grid. A
// resize is the only place the value is read, and starting one is rare enough
// to re-read the element here.
export const syncResizeDirection = (
  table: ResizeDirectionTable,
  element: Element | null | undefined
): void => {
  const direction = isElementRTL(element) ? 'rtl' : 'ltr'
  if (table.options.columnResizeDirection !== direction) {
    table.setOptions(previous => ({ ...previous, columnResizeDirection: direction }) as never)
  }
}
